import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

import {styled} from "@mui/material/styles";

import CatalogSummaryCard from "./CatalogSummaryCard";
import CatalogSummaryPeriodSelect, {periodLabelId} from "./CatalogSummaryPeriodSelect";
import catalogSummary from "api/product/catalogSummary";
import {black500, greyishBlue600} from "assets/jss/main";

CatalogSummary.propTypes = {
    // bare retailer uuid; the block renders nothing without one (organization-wide view)
    retailerId: PropTypes.string,
    // receives the card's filter params: clears every active filter, then applies these
    onFilter: PropTypes.func,
};

/**
 * The five cards are declared once here: label, tooltip, the API key they read, and the
 * list filters a click applies. The disabled card replaces the "vs <period>" caption with
 * the causes of a deactivation.
 *
 * `filters` uses the FRONT url vocabulary, which retailerListing.js translates for the
 * Elasticsearch endpoint (exists[eshopId] becomes onEshop). A card without filters is not
 * clickable: its list equivalent is not decided yet, and a click that silently filtered on
 * something else would be worse than no click at all.
 */
const CARDS = [
    {
        key: 'online',
        labelId: 'retailer.product.catalogSummary.online',
        // the card's counter is status AND validated AND on-eshop AND in stock
        // (ProductRepository online_products): the click applies the same four
        filters: ['status=true', 'validated=true', 'exists[eshopId]=true', 'inStock=true'],
    },
    {key: 'potential', labelId: 'retailer.product.catalogSummary.potential'},
    {key: 'enriched', labelId: 'retailer.product.catalogSummary.enriched', hidden: true},
    {
        key: 'disabled',
        labelId: 'retailer.product.catalogSummary.disabled',
        footerId: 'retailer.product.catalogSummary.disabled.causes',
        filters: ['status=false'],
    },
    {key: 'toEnrich', labelId: 'retailer.product.catalogSummary.toEnrich', hidden: true},
];

const Container = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '16px',
    width: '100%',
    // Separates the block from the filters and toolbar it sits above; the block is always
    // a page section, so it carries that spacing rather than every caller repeating it.
    marginBottom: '24px'
});

const Header = styled('div')({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '4px',
    width: '100%'
});

const Headings = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    flex: '1 1 0',
    minWidth: 0
});

const Title = styled('span')({
    color: black500,
    fontSize: '20px',
    fontWeight: 600
});

const Subtitle = styled('span')({
    color: greyishBlue600,
    fontSize: '14px'
});

const Cards = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'stretch',
    gap: '16px',
    width: '100%',
    [theme.breakpoints.down('md')]: {
        overflowX: 'auto'
    }
}));

function CatalogSummary({retailerId, onFilter}) {
    const [period, setPeriod] = useState('week');
    const [cards, setCards] = useState(null);

    const load = useCallback((selectedPeriod) => {
        if (!retailerId) {
            return;
        }

        catalogSummary(retailerId, selectedPeriod)
            .then((summary) => setCards(summary.cards ?? null))
            // A failed summary must not break the product list underneath it: the cards
            // fall back to their empty state.
            .catch(() => setCards(null));
    }, [retailerId]);

    useEffect(() => {
        load(period);
    }, [load, period]);

    if (!retailerId) {
        return null;
    }

    return (
        <Container>
            <Header>
                <Headings>
                    <Title><FormattedMessage id={"retailer.product.catalogSummary.title"}/></Title>
                    <Subtitle><FormattedMessage id={"retailer.product.catalogSummary.subtitle"}/></Subtitle>
                </Headings>
                <CatalogSummaryPeriodSelect value={period} onChange={setPeriod}/>
            </Header>
            <Cards>
                {CARDS.filter(({hidden}) => !hidden).map(({key, labelId, footerId, filters}) => (
                    <CatalogSummaryCard
                        key={key}
                        labelId={labelId}
                        tooltipId={`${labelId}.tooltip`}
                        value={cards?.[key]?.value ?? null}
                        variation={cards?.[key]?.variation ?? null}
                        footerId={footerId}
                        periodLabelId={periodLabelId(period)}
                        onClick={filters && onFilter ? () => onFilter(filters) : undefined}
                    />
                ))}
            </Cards>
        </Container>
    );
}

export default CatalogSummary;
